import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { Id } from "../../../convex/_generated/dataModel";
import { toast } from "sonner";
import { Button } from "../../reactvideoeditor/pro/components/ui/button";
import { Input } from "../../reactvideoeditor/pro/components/ui/input";
import { X, Upload, Edit, Trash2, Music, Volume2 } from "lucide-react";

/**
 * Extract audio duration from file using HTML5 Audio API
 */
const extractAudioDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const audio = new Audio();
    const url = URL.createObjectURL(file);

    audio.addEventListener("loadedmetadata", () => {
      URL.revokeObjectURL(url);
      resolve(audio.duration);
    });

    audio.addEventListener("error", (e) => {
      URL.revokeObjectURL(url);
      reject(new Error("Failed to load audio file"));
    });

    audio.src = url;
  });
};

export function AudioUploadPanel() {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [audioType, setAudioType] = useState<"music" | "sfx">("sfx");
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [artist, setArtist] = useState("");
  const [duration, setDuration] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [editingId, setEditingId] = useState<Id<"audioLibrary"> | null>(null);

  const generateUploadUrl = useMutation(api.files.generateUploadUrl);
  const uploadAudio = useMutation(api.audioLibrary.uploadAudio);
  const updateAudioTags = useMutation(api.audioLibrary.updateAudioTags);
  const deleteAudio = useMutation(api.audioLibrary.deleteAudio);
  const allAudio = useQuery(api.audioLibrary.searchAudio, {});
  const allTags = useQuery(api.audioLibrary.getAllTags, {});

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Extract duration when file is selected
  useEffect(() => {
    if (selectedFile) {
      extractAudioDuration(selectedFile)
        .then((dur) => {
          setDuration(dur);
        })
        .catch((error) => {
          console.error("Failed to extract duration:", error);
          toast.error("Failed to extract audio duration");
        });
    }
  }, [selectedFile]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["audio/mpeg", "audio/wav", "audio/ogg", "audio/mp4", "audio/m4a"];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|ogg|m4a)$/i)) {
      toast.error("Invalid file type. Please select an audio file (mp3, wav, ogg, m4a)");
      return;
    }

    // Validate file size (max 50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File size exceeds 50MB limit");
      return;
    }

    setSelectedFile(file);
    // Auto-fill name from filename if name is empty
    if (!name) {
      setName(file.name.replace(/\.[^/.]+$/, ""));
    }
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select an audio file");
      return;
    }

    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }

    if (tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }

    setIsUploading(true);

    try {
      // Generate upload URL
      const uploadUrl = await generateUploadUrl();

      // Upload file to Convex storage
      const uploadResult = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": selectedFile.type },
        body: selectedFile,
      });

      if (!uploadResult.ok) {
        throw new Error("Failed to upload file");
      }

      // Convex storage returns the storageId as JSON
      const { storageId } = await uploadResult.json();

      // Create audio library entry
      await uploadAudio({
        storageId: storageId as Id<"_storage">,
        name: name.trim(),
        audioType,
        tags,
        duration: duration || undefined,
        artist: artist.trim() || undefined,
      });

      toast.success("Audio uploaded successfully!");
      
      // Reset form
      setSelectedFile(null);
      setName("");
      setTags([]);
      setTagInput("");
      setArtist("");
      setDuration(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload audio: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleEdit = (audio: any) => {
    setEditingId(audio._id);
    setName(audio.name);
    setAudioType(audio.audioType);
    setTags(audio.tags || []);
    setArtist(audio.artist || "");
    setSelectedFile(null);
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;

    if (!name.trim()) {
      toast.error("Please enter a name");
      return;
    }

    if (tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }

    try {
      await updateAudioTags({
        audioId: editingId,
        name: name.trim(),
        tags,
        artist: artist.trim() || undefined,
      });

      toast.success("Audio updated successfully!");
      setEditingId(null);
      setName("");
      setTags([]);
      setTagInput("");
      setArtist("");
      setSelectedFile(null);
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update audio: " + (error instanceof Error ? error.message : "Unknown error"));
    }
  };

  const handleDelete = async (audioId: Id<"audioLibrary">) => {
    if (!confirm("Are you sure you want to delete this audio?")) {
      return;
    }

    try {
      await deleteAudio({ audioId });
      toast.success("Audio deleted successfully!");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete audio: " + (error instanceof Error ? error.message : "Unknown error"));
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setName("");
    setTags([]);
    setTagInput("");
    setArtist("");
    setSelectedFile(null);
  };

  // Filter tag suggestions
  const tagSuggestions = allTags?.filter(
    (tag) => !tags.includes(tag) && tag.toLowerCase().includes(tagInput.toLowerCase())
  ) || [];

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <h1 className="text-2xl font-bold mb-6">Audio Library Management</h1>

      {/* Upload Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">
          {editingId ? "Edit Audio" : "Upload New Audio"}
        </h2>

        <div className="space-y-4">
          {/* File Upload */}
          {!editingId && (
            <div>
              <label className="block text-sm font-medium mb-2">Audio File</label>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/*"
                onChange={handleFileSelect}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
              />
              {selectedFile && (
                <p className="mt-2 text-sm text-gray-600">
                  Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
              {duration !== null && (
                <p className="mt-1 text-sm text-gray-600">Duration: {duration.toFixed(2)}s</p>
              )}
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2">Name *</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter audio name"
            />
          </div>

          {/* Audio Type */}
          <div>
            <label className="block text-sm font-medium mb-2">Audio Type *</label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="music"
                  checked={audioType === "music"}
                  onChange={(e) => setAudioType(e.target.value as "music" | "sfx")}
                />
                <Music className="w-4 h-4" />
                <span>Music</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  value="sfx"
                  checked={audioType === "sfx"}
                  onChange={(e) => setAudioType(e.target.value as "music" | "sfx")}
                />
                <Volume2 className="w-4 h-4" />
                <span>SFX</span>
              </label>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2">Tags *</label>
            <div className="flex gap-2 mb-2">
              <Input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleTagInputKeyDown}
                placeholder="Enter tag and press Enter"
                className="flex-1"
              />
              <Button onClick={handleAddTag} type="button" size="sm">
                Add
              </Button>
            </div>
            {/* Tag suggestions */}
            {tagSuggestions.length > 0 && tagInput && (
              <div className="mb-2 flex flex-wrap gap-1">
                {tagSuggestions.slice(0, 5).map((tag) => (
                  <button
                    key={tag}
                    onClick={() => {
                      if (!tags.includes(tag)) {
                        setTags([...tags, tag]);
                        setTagInput("");
                      }
                    }}
                    className="text-xs px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded border border-gray-300"
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
            {/* Selected tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-800 rounded text-sm"
                  >
                    {tag}
                    <button
                      onClick={() => handleRemoveTag(tag)}
                      className="hover:text-orange-900"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Artist (optional) */}
          <div>
            <label className="block text-sm font-medium mb-2">Artist (optional)</label>
            <Input
              type="text"
              value={artist}
              onChange={(e) => setArtist(e.target.value)}
              placeholder="Enter artist name"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            {editingId ? (
              <>
                <Button onClick={handleSaveEdit} disabled={isUploading}>
                  Save Changes
                </Button>
                <Button onClick={handleCancelEdit} variant="outline">
                  Cancel
                </Button>
              </>
            ) : (
              <Button
                onClick={handleUpload}
                disabled={isUploading || !selectedFile}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isUploading ? (
                  <>
                    <Upload className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload Audio
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Audio List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold mb-4">Audio Library</h2>
        {allAudio === undefined ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        ) : allAudio.items.length === 0 ? (
          <p className="text-gray-500 text-center py-8">No audio files uploaded yet</p>
        ) : (
          <div className="space-y-2">
            {allAudio.items.map((audio) => (
              <div
                key={audio._id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium">{audio.name}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-100 rounded">
                      {audio.audioType}
                    </span>
                  </div>
                  {audio.artist && (
                    <p className="text-sm text-gray-600 mb-1">Artist: {audio.artist}</p>
                  )}
                  {audio.duration && (
                    <p className="text-xs text-gray-500 mb-2">Duration: {audio.duration.toFixed(2)}s</p>
                  )}
                  {audio.tags && audio.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {audio.tags.map((tag) => (
                        <span
                          key={tag}
                          className="text-xs px-2 py-0.5 bg-orange-100 text-orange-800 rounded"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleEdit(audio)}
                    variant="outline"
                    size="sm"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => handleDelete(audio._id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

